import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { resizePhoto } from "@/lib/imageResize";
import { SortableGallery } from "@/components/staff/SortableGallery";
import { useTranslation } from "react-i18next";
import { LogOut, Trash2, Plus, Download, Image as ImageIcon, FileSpreadsheet } from "lucide-react";

const TABS = ["planimetries", "bookings", "slots", "portfolio", "services"];

export default function StaffDashboard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("planimetries");
  const token = typeof window !== "undefined" ? localStorage.getItem("staff_token") : null;

  useEffect(() => { if (!token) nav("/staff/login"); }, [token, nav]);

  const logout = () => { localStorage.removeItem("staff_token"); nav("/staff/login"); };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-stone-900">Plan It <span className="text-[#D06D4D]">Studio</span></Link>
          <button data-testid="staff-logout" onClick={logout} className="text-sm text-stone-700 hover:text-stone-900 inline-flex items-center gap-2">
            <LogOut size={14} /> {t("staff.logout")}
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-serif text-4xl text-stone-900" data-testid="staff-dashboard-title">{t("staff.dashboard")}</h1>
        <div className="mt-8 border-b border-stone-200 flex gap-6 overflow-x-auto">
          {TABS.map((k) => (
            <button
              key={k}
              data-testid={`staff-tab-${k}`}
              onClick={() => setTab(k)}
              className={`pb-3 text-sm font-medium transition-colors whitespace-nowrap ${tab === k ? "text-stone-900 border-b-2 border-[#D06D4D]" : "text-stone-500 hover:text-stone-900"}`}
            >
              {t(`staff.tabs.${k}`)}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "planimetries" && <PlanTable />}
          {tab === "bookings" && <BookingsTable />}
          {tab === "slots" && <SlotsManager />}
          {tab === "portfolio" && <PortfolioManager />}
          {tab === "services" && <ServicesManager />}
        </div>
      </div>
    </div>
  );
}

const PKG_LABEL = {
  planit_draw_base: { name: "Disegno base", price: "€34,90" },
  planit_draw_detail: { name: "Disegno dettagliato", price: "€69,90" },
};
const PAY_LABEL = { stripe: "Carta (Stripe)", bonifico: "Bonifico", paypal: "PayPal" };
const ROOM_LABEL = {
  living: "Soggiorno", kitchen: "Cucina", bedroom: "Camera da letto",
  kids: "Cameretta", bathroom: "Bagno", office: "Studio",
  entrance: "Ingresso", dining: "Sala da pranzo", other: "Altro",
};

function PlanTable() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/staff/planimetries").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const url = (p) => `${API}/files/${p}`;
  const paidCount = items.filter((i) => i.payment_status === "paid").length;

  const markPaid = async (id) => {
    if (!window.confirm("Confermi di aver ricevuto il pagamento? Verrà inviata la conferma al cliente.")) return;
    try {
      await api.post(`/staff/planimetries/${id}/mark-paid`);
      toast.success("Pagamento registrato — email inviata al cliente");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const exportCSV = async () => {
    try {
      const res = await api.get("/staff/planimetries/paid.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `planit-pagamenti-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error("Errore export CSV");
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-plan-list">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">{items.length} progetti totali · <span className="text-emerald-700 font-semibold">{paidCount} pagati</span></p>
        <button data-testid="staff-export-csv" onClick={exportCSV} disabled={paidCount === 0} className="inline-flex items-center gap-2 text-sm bg-stone-900 hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-full font-semibold">
          <FileSpreadsheet size={14} /> Esporta pagamenti CSV
        </button>
      </div>
      {items.length === 0 && <p className="text-stone-500">Nessuna richiesta.</p>}
      {items.map((p) => {
        const pkg = PKG_LABEL[p.package] || null;
        const pay = PAY_LABEL[p.payment_method] || p.payment_method;
        const paid = p.payment_status === "paid";
        const room = ROOM_LABEL[p.room_type] || p.room_type;
        return (
          <div key={p.id} className="bg-white border border-stone-200 p-6 grid grid-cols-1 md:grid-cols-4 gap-6" data-testid={`staff-plan-${p.id}`}>
            <div className="md:col-span-1">
              {p.png_path ? (
                <a href={url(p.png_path)} target="_blank" rel="noopener noreferrer" data-testid={`staff-plan-thumb-${p.id}`}>
                  <img alt="preview" src={url(p.png_path)} className="w-full aspect-square object-contain bg-stone-50 border border-stone-200 rounded hover:border-[#D06D4D] transition-colors" />
                </a>
              ) : (
                <div className="w-full aspect-square bg-stone-100 flex items-center justify-center text-stone-400 rounded"><ImageIcon size={32} /></div>
              )}
              {(p.photo_urls || []).length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-1">
                  {(p.photo_urls || []).slice(0, 3).map((u, i) => (
                    <a key={u} href={url(u)} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={url(u)} alt={`foto-${i}`} className="w-full aspect-square object-cover rounded border border-stone-200 hover:border-[#D06D4D]" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="md:col-span-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="eyebrow">{p.mode} · {new Date(p.created_at).toLocaleDateString("it-IT", { day:"2-digit", month:"short", year:"numeric" })}</span>
                {pkg && <span className="text-[10px] uppercase tracking-widest bg-stone-900 text-white px-2 py-0.5 rounded-full">{pkg.name} — {pkg.price}{(p.quantity || 1) > 1 ? ` × ${p.quantity}` : ""}</span>}
                {p.payment_method && (
                  <span data-testid={`staff-plan-paystatus-${p.id}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${paid ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
                    {pay} · {paid ? "pagato" : "in attesa"}
                  </span>
                )}
              </div>
              <div className="mt-3 font-serif text-xl text-stone-900">{p.name}</div>
              <div className="text-stone-600">{p.email} · {p.phone}</div>
              <div className="mt-3 text-stone-700 space-y-0.5">
                <div><strong>Intervento:</strong> {p.intervention_type || "—"}</div>
                {room && <div><strong>Stanza:</strong> {room}</div>}
                {p.notes && <div><strong>Note:</strong> {p.notes}</div>}
                {paid && p.paid_at && <div className="text-emerald-700 text-xs mt-1">✓ Pagamento registrato il {new Date(p.paid_at).toLocaleDateString("it-IT")}</div>}
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {(p.payment_method === "bonifico" || p.payment_method === "paypal") && !paid && (
                <button data-testid={`staff-plan-markpaid-${p.id}`} onClick={() => markPaid(p.id)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-center py-2 rounded-full inline-flex items-center justify-center gap-2 font-semibold">
                  ✓ Segna come pagato
                </button>
              )}
              {p.dxf_path && (
                <a data-testid={`staff-plan-dxf-${p.id}`} href={url(p.dxf_path)} target="_blank" rel="noopener noreferrer" className="block bg-stone-900 text-white text-center py-2 rounded-full hover:bg-stone-800 inline-flex items-center justify-center gap-2 w-full">
                  <Download size={14} /> DXF
                </a>
              )}
              {p.png_path && (
                <a data-testid={`staff-plan-png-${p.id}`} href={url(p.png_path)} target="_blank" rel="noopener noreferrer" className="block border border-stone-900 text-stone-900 text-center py-2 rounded-full hover:bg-stone-100 inline-flex items-center justify-center gap-2 w-full">
                  <Download size={14} /> PNG
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingsTable() {
  const [items, setItems] = useState([]);
  const { t } = useTranslation();
  const load = useCallback(() => api.get("/staff/bookings").then((r) => setItems(r.data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const setStatus = async (id, status) => {
    try { await api.patch(`/staff/bookings/${id}`, { status }); toast.success("OK"); load(); } catch { toast.error("Error"); }
  };
  return (
    <div className="bg-white border border-stone-200" data-testid="staff-bookings-list">
      <table className="w-full text-sm">
        <thead className="bg-stone-50">
          <tr className="text-left text-xs uppercase tracking-widest text-stone-500">
            <th className="p-4">Data</th><th className="p-4">Cliente</th><th className="p-4">Contatti</th><th className="p-4">Motivo</th><th className="p-4">{t("staff.bookings.status")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} className="border-t border-stone-100" data-testid={`staff-booking-${b.id}`}>
              <td className="p-4 font-medium">{b.slot_date}<br /><span className="text-stone-500">{b.slot_start}–{b.slot_end}</span></td>
              <td className="p-4">{b.name}</td>
              <td className="p-4 text-stone-600">{b.email}<br />{b.phone}</td>
              <td className="p-4 text-stone-600">{b.reason || "—"}</td>
              <td className="p-4">
                <select data-testid={`staff-booking-status-${b.id}`} value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} className="border border-stone-300 px-2 py-1 rounded text-xs">
                  <option value="pending">{t("staff.bookings.pending")}</option>
                  <option value="confirmed">{t("staff.bookings.confirmed")}</option>
                  <option value="completed">{t("staff.bookings.completed")}</option>
                  <option value="cancelled">{t("staff.bookings.cancelled")}</option>
                </select>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-stone-500">Nessuna prenotazione.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SlotsManager() {
  useEffect(() => {
    if (document.getElementById("calendly-widget-script")) return;
    const s = document.createElement("script");
    s.id = "calendly-widget-script";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="space-y-6">
      <a
        href="https://calendly.com/planit-studio/teatro-dinamico"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="staff-slots-calendly-link"
        className="flex items-center justify-between gap-4 bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-6 py-4 rounded-2xl transition-colors"
      >
        <div>
          <div className="font-serif text-xl">Apri il calendario Calendly</div>
          <div className="text-xs uppercase tracking-widest text-white/80 mt-1">Prenotazioni pubbliche Teatro Dinamico</div>
        </div>
        <span className="text-sm font-semibold inline-flex items-center gap-1">calendly.com/planit-studio/teatro-dinamico →</span>
      </a>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="staff-slots-calendly-embed">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl text-stone-900">Calendario appuntamenti</h3>
            <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">Vista in tempo reale dalla tua agenda Calendly</p>
          </div>
        </div>
        <div
          className="calendly-inline-widget"
          data-url="https://calendly.com/planit-studio/teatro-dinamico?embed_type=Inline&hide_gdpr_banner=1&background_color=ffffff&text_color=1c1917&primary_color=d06d4d"
          style={{ minWidth: 320, height: 780 }}
        />
      </div>
    </div>
  );
}

function PortfolioManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optProgress, setOptProgress] = useState("");
  const empty = { title_it: "", title_en: "", description_it: "", description_en: "", category: "residential", year: new Date().getFullYear(), cover_url: "", image_urls: [] };
  const { t } = useTranslation();

  const load = useCallback(() => api.get("/portfolio").then((r) => setItems(r.data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/staff/portfolio/${editing.id}`, editing);
      else await api.post("/staff/portfolio", editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch { toast.error("Error"); }
  };
  const del = async (id) => { if (!window.confirm("Confermi?")) return; await api.delete(`/staff/portfolio/${id}`); load(); };

  const allPhotos = editing ? [editing.cover_url, ...(editing.image_urls || [])].filter(Boolean) : [];
  const setPhotos = (arr) => setEditing((ed) => ({ ...ed, cover_url: arr[0] || "", image_urls: arr.slice(1) }));

  const uploadImg = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", await resizePhoto(file));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const publicUrl = `${API}/files/${data.path}`;
        setEditing((ed) => (ed.cover_url ? { ...ed, image_urls: [...(ed.image_urls || []), publicUrl] } : { ...ed, cover_url: publicUrl }));
      }
      toast.success(files.length > 1 ? `${files.length} foto caricate` : "Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento foto");
    } finally { setUploading(false); e.target.value = ""; }
  };
  const optimizeAll = async () => {
    setOptimizing(true);
    try {
      let st = (await api.post("/staff/images/optimize")).data;
      while (st.running) {
        await new Promise((r) => setTimeout(r, 3000));
        st = (await api.get("/staff/images/optimize")).data;
        setOptProgress(`${st.done}/${st.total}`);
      }
      toast.success(`Ottimizzate ${st.optimized} foto${st.errors ? `, ${st.errors} errori` : ""}`);
      load();
    } catch { toast.error("Errore ottimizzazione"); } finally { setOptimizing(false); setOptProgress(""); }
  };

  if (editing) {
    return (
      <div className="bg-white border border-stone-200 p-8 max-w-3xl" data-testid="staff-portfolio-form">
        <h3 className="font-serif text-2xl text-stone-900">{editing.id ? t("staff.portfolio.edit") : t("staff.portfolio.add")}</h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {[["title_it", "Titolo IT"], ["title_en", "Title EN"]].map(([k, l]) => (
            <div key={k}><label className="block text-xs text-stone-600">{l}</label>
              <input data-testid={`staff-portfolio-${k}`} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="w-full border border-stone-300 px-3 py-2 rounded" />
            </div>
          ))}
          {[["description_it", "Descrizione IT"], ["description_en", "Description EN"]].map(([k, l]) => (
            <div key={k} className="md:col-span-2"><label className="block text-xs text-stone-600">{l}</label>
              <textarea data-testid={`staff-portfolio-${k}`} rows={3} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="w-full border border-stone-300 px-3 py-2 rounded resize-none" />
            </div>
          ))}
          <div><label className="block text-xs text-stone-600">Categoria</label>
            <select data-testid="staff-portfolio-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-stone-300 px-3 py-2 rounded">
              <option value="residential">Residenziale</option>
              <option value="commercial">Commerciale</option>
              <option value="interior">Interior Design</option>
            </select>
          </div>
          <div><label className="block text-xs text-stone-600">Anno</label>
            <input data-testid="staff-portfolio-year" type="number" value={editing.year || ""} onChange={(e) => setEditing({ ...editing, year: parseInt(e.target.value || "0") })} className="w-full border border-stone-300 px-3 py-2 rounded" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-stone-600 mb-2">Foto (la prima è la copertina · trascina per riordinare · tocca ★ per scegliere la copertina)</label>
            {allPhotos.length > 0 && (
              <div className="mb-3">
                <SortableGallery urls={allPhotos} onChange={setPhotos} testPrefix="staff-portfolio" />
              </div>
            )}
            <input data-testid="staff-portfolio-gallery-upload" type="file" accept="image/*,.heic,.heif" multiple disabled={uploading} onChange={(e) => uploadImg(e)} className="text-xs" />
            {uploading && <p className="text-xs text-stone-500 mt-1" data-testid="staff-portfolio-uploading">Caricamento e ottimizzazione foto…</p>}
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="staff-portfolio-save" onClick={save} className="bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-6 py-2 rounded-full text-sm">{t("staff.portfolio.save")}</button>
          <button data-testid="staff-portfolio-cancel" onClick={() => setEditing(null)} className="border border-stone-300 text-stone-800 px-6 py-2 rounded-full text-sm">{t("staff.portfolio.cancel")}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="staff-portfolio-new" onClick={() => setEditing(empty)} className="bg-stone-900 text-white px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"><Plus size={14} /> {t("staff.portfolio.add")}</button>
        <button data-testid="staff-images-optimize" onClick={optimizeAll} disabled={optimizing} className="border border-stone-300 text-stone-800 px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"><ImageIcon size={14} /> {optimizing ? `Ottimizzazione in corso… ${optProgress}` : "Ottimizza tutte le foto"}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="staff-portfolio-list">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-stone-200" data-testid={`staff-portfolio-${p.id}`}>
            {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-40 object-cover" />}
            <div className="p-4">
              <div className="font-serif text-lg text-stone-900">{p.title_it}</div>
              <div className="text-xs text-stone-500 uppercase tracking-widest">{p.category} · {p.year}</div>
              <div className="mt-3 flex gap-2">
                <button data-testid={`staff-portfolio-edit-${p.id}`} onClick={() => setEditing(p)} className="text-xs bg-stone-900 text-white px-3 py-1 rounded-full">{t("staff.portfolio.edit")}</button>
                <button data-testid={`staff-portfolio-delete-${p.id}`} onClick={() => del(p.id)} className="text-xs border border-stone-300 px-3 py-1 rounded-full text-red-600 inline-flex items-center gap-1"><Trash2 size={12} /> {t("staff.portfolio.delete")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SERVICES_DEF = [
  { slug: "progettazione-architettonica", title: "Progettazione Architettonica" },
  { slug: "spazio-dimostrativo", title: "Teatro Dinamico" },
  { slug: "design-d-interni", title: "Design d'Interni" },
  { slug: "consulenza-e-planimetrie", title: "Consulenza e Planimetrie" },
  { slug: "ristrutturazioni", title: "Ristrutturazioni" },
  { slug: "direzione-lavori-e-sicurezza", title: "Direzione Lavori e Sicurezza" },
];
const MAX_SERVICE_GALLERY = 10;

function ServicesManager() {
  const [galleries, setGalleries] = useState({});
  const [uploading, setUploading] = useState(null);

  const load = useCallback(() => api.get("/services/gallery").then((r) => setGalleries(r.data || {})), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async (slug, urls) => {
    try {
      const { data } = await api.put(`/staff/services/${slug}/gallery`, { image_urls: urls });
      setGalleries((g) => ({ ...g, [slug]: data.image_urls }));
      toast.success("Galleria aggiornata");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onUpload = async (slug, files) => {
    if (!files || !files.length) return;
    const current = galleries[slug] || [];
    const free = MAX_SERVICE_GALLERY - current.length;
    if (free <= 0) { toast.error(`Massimo ${MAX_SERVICE_GALLERY} foto per servizio`); return; }
    const toUpload = Array.from(files).slice(0, free);
    setUploading(slug);
    const newUrls = [];
    for (const f of toUpload) {
      try {
        const fd = new FormData(); fd.append("file", await resizePhoto(f));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${API}/files/${data.path}`);
      } catch { toast.error(`Errore caricamento ${f.name}`); }
    }
    setUploading(null);
    if (newUrls.length) await save(slug, [...current, ...newUrls]);
  };

  return (
    <div className="space-y-8" data-testid="staff-services-list">
      {SERVICES_DEF.map((s) => {
        const imgs = galleries[s.slug] || [];
        const isUp = uploading === s.slug;
        return (
          <div key={s.slug} className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[8px_8px_24px_rgba(28,25,23,0.06)]" data-testid={`staff-service-${s.slug}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <h3 className="font-serif text-xl text-stone-900">{s.title}</h3>
              <div className="text-xs uppercase tracking-widest text-stone-500">{imgs.length}/{MAX_SERVICE_GALLERY}</div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <label className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full cursor-pointer transition-colors ${imgs.length >= MAX_SERVICE_GALLERY ? "bg-stone-200 text-stone-500 cursor-not-allowed" : "bg-[#D06D4D] text-white hover:bg-[#b95a3d]"}`}>
                <Plus size={14} /> {isUp ? "Caricamento…" : "Aggiungi foto"}
                <input
                  data-testid={`staff-service-upload-${s.slug}`}
                  type="file"
                  accept="image/*,.heic,.heif"
                  multiple
                  disabled={isUp || imgs.length >= MAX_SERVICE_GALLERY}
                  onChange={(e) => onUpload(s.slug, e.target.files)}
                  className="hidden"
                />
              </label>
              {imgs.length > 0 && (
                <button onClick={() => save(s.slug, [])} className="text-xs text-stone-500 hover:text-red-600 underline">Svuota galleria</button>
              )}
            </div>
            {imgs.length === 0 ? (
              <p className="mt-4 text-sm text-stone-500">Nessuna foto caricata.</p>
            ) : (
              <div className="mt-5">
                <p className="text-xs text-stone-500 mb-2">Trascina per riordinare · la prima foto è la copertina della scheda · tocca ★ per impostarla</p>
                <SortableGallery urls={imgs} onChange={(arr) => save(s.slug, arr)} testPrefix={`staff-service-${s.slug}`} coverLabel={s.slug === "spazio-dimostrativo" ? "Copertina Home" : "Copertina"} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
